import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components/macro";
import * as yup from "yup";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Logo } from "../../components/Logo/Logo";

const SUBMISSION = "__SUBMISSION__";

interface FormData {
  password: string;
  confirmation: string;
  [SUBMISSION]: void;
}

const schema = yup
  .object({
    password: yup.string().required(),
    confirmation: yup
      .string()
      .oneOf([yup.ref("password")])
      .required(),
  })
  .required();

export function PasswordResetPage() {
  const { token } = useParams();
  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      password: "",
      confirmation: "",
    },
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    clearErrors(SUBMISSION);
    const requestParams = new URLSearchParams();
    requestParams.set("password", data.password);

    try {
      const body = await fetch(`/api/reset/${token}`, {
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: requestParams.toString(),
      });
      const res = await body.json();

      if (res.ok) {
        document.location.pathname = "/";
        return;
      }

      if (res.error === "NO_USER_TOKEN") {
        setError(SUBMISSION, {
          type: "submission",
          message:
            "Token have been expired. Please, request new reset password link.",
        });
        return;
      }
    } catch (err) {}

    setError(SUBMISSION, {
      type: "submission",
      message: "Something went wrong. Try again later.",
    });
  });

  return (
    <Container>
      <Panel>
        <LogoLink href="/">
          <Logo />
        </LogoLink>
        <SignUpSection>
          <span>Not a member?</span>{" "}
          <PanelLink to="/auth/signup">Sign up now</PanelLink>
        </SignUpSection>
      </Panel>
      <Content>
        <h1>Password recovery</h1>
        <form onSubmit={handleFormSubmit}>
          <InputRow>
            <Input
              icon="lock"
              type="password"
              placeholder="Password"
              hasError={!!errors.password}
              {...register("password")}
            />
          </InputRow>
          <InputRow>
            <Input
              icon="lock"
              type="password"
              placeholder="Confirm password"
              hasError={!!errors.confirmation}
              {...register("confirmation")}
            />
          </InputRow>
          <ButtonsRow>
            <Button
              $kind="contained"
              $size="l"
              $fullWidth
              disabled={isSubmitting}
            >
              Continue
            </Button>
          </ButtonsRow>
          {errors[SUBMISSION] && (
            <ErrorMsg>{errors[SUBMISSION].message}</ErrorMsg>
          )}
        </form>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Panel = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
  flex: 0 0 ${(p) => p.theme.panelHeight};
  height: ${(p) => p.theme.panelHeight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled.a`
  svg {
    display: block;
  }
`;

const SignUpSection = styled.div`
  @media screen and (max-width: 440px) {
    span {
      display: none;
    }
  }
`;

const PanelLink = styled(Link)`
  color: ${(p) => p.theme.colors.ui.purple};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Content = styled.div`
  flex: 1 0 auto;
  padding-top: 40px;
  width: 400px;
  text-align: center;

  h1 {
    margin-bottom: 24px;
  }
`;

const InputRow = styled.div`
  margin-bottom: 8px;

  label {
    width: 100%;
  }
`;

const ButtonsRow = styled.div`
  margin-top: 16px;
`;

const ErrorMsg = styled.div`
  margin-top: 4px;
  text-align: center;
  color: ${(p) => p.theme.colors.statuses.error};
`;
